import React from "react";

import { getOptions, getFirstOption, getBaseUnits, getUnitOptions } from "utils/fields";
import { getDefaultFurnaceSpecs } from "utils/mechanicals";
import { maxLength, getDecimalPlaces } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";

import classes from "./style.module.scss";

const charLim100 = maxLength(100);
const furnaceEnerSourceValidation = "furnaceEnergySource";
const boilerEnerSourceValidation = "boilerEnergySource";
const furnaceEquipTypeValidation = "furnaceEquipType";
const boilerEquipTypeValidation = "boilerEquipType";
const furnaceSwitchTempValidation = "furnaceSwitchoverTemp";
const boilerSwitchTempValidation = "boilerSwitchoverTemp";

const furnaceCapacityValidation = "furnaceOutputCapacity";
const boilerCapacityValidation = "boilerOutputCapacity";

const furnaceSizingFactorValidation = "furnaceSizingFactor";
const boilerSizingFactorValidation = "boilerSizingFactor";

const furnaceEffValidation = "furnaceEff";
const boilerEffValidation = "boilerEff";

const furnacePilotValidation = "furnacePilotLight";
const boilerPilotValidation = "boilerPilotLight";
const furnaceFlueValidation = "furnaceFlueDiameter";
const boilerFlueValidation = "boilerFlueDiameter";

const selectValidation = ({ system, field }) =>
    ({
        furnace: {
            energySource: furnaceEnerSourceValidation,
            equipType: furnaceEquipTypeValidation,
            switchoverTemp: furnaceSwitchTempValidation,
            outputCapacity: furnaceCapacityValidation,
            sizingFactor: furnaceSizingFactorValidation,
            efficiency: furnaceEffValidation,
            pilotLight: furnacePilotValidation,
            flueDiameter: furnaceFlueValidation,
        },
        boiler: {
            energySource: boilerEnerSourceValidation,
            equipType: boilerEquipTypeValidation,
            switchoverTemp: boilerSwitchTempValidation,
            outputCapacity: boilerCapacityValidation,
            sizingFactor: boilerSizingFactorValidation,
            efficiency: boilerEffValidation,
            pilotLight: boilerPilotValidation,
            flueDiameter: boilerFlueValidation,
        },
    }[system][field]);

export default ({ accessor, change }) => {
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const { id: yearBuiltId, value: yearBuiltValue } =
        useStateSelector(`model.modelData.specifications.yearBuilt`) || {};

    const currentPrimarySystem = Object.keys(useStateSelector(`${accessor}.system`) || {})[0];

    //accessor looks like: modelData.heatingCooling.heating OR fields.heating

    const coolingAccessor = `${accessor.slice(0, -8)}.cooling.system`;
    const coolingSystem = useStateSelector(coolingAccessor) || {};
    const coolingSystemType = Object.keys(coolingSystem)[0] || "";
    const disableDualFuel = coolingSystemType.includes("HeatPump");

    const { id: energySourceId } = useStateSelector(`${accessor}.system.${currentPrimarySystem}.energySource`) || {};
    const { id: equipTypeId } = useStateSelector(`${accessor}.system.${currentPrimarySystem}.equipmentType`) || {};
    const isDualFuel = useStateSelector(`${accessor}.system.${currentPrimarySystem}.isBiEnergy`);
    const { id: capacityId, value: capacityValue } =
        useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.outputCapacity`) || {};
    const effValue = useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.efficiency`) || 0;
    const isSteadyState =
        useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.isSteadyState`) || false;
    const pilotLightValue = useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.pilotLight`);
    const flueDiameterValue = useStateSelector(
        `${accessor}.system.${currentPrimarySystem}.specifications.flueDiameter`
    );

    const capacityUnits = useStateSelector(
        `${accessor}.system.${currentPrimarySystem}.specifications.outputCapacity_u`
    );
    const pilotUnits = useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.pilotLight_u`);
    const flueUnits = useStateSelector(`${accessor}.system.${currentPrimarySystem}.specifications.flueDiameter_u`);
    const switchoverTempUnits = useStateSelector(`${accessor}.system.${currentPrimarySystem}.switchoverTemp_u`);

    let isRecentVintage = [9, 10].includes(yearBuiltId) || yearBuiltValue > 1987;

    return (
        <>
            <InputRow gridTemplate="23.4% 23.4% 10rem 23.4%">
                <Field
                    component={Input}
                    name={`${accessor}.system.${currentPrimarySystem}.manufacturer`}
                    type="text"
                    label="Manufacturer Name"
                    placeholder="Enter Manufacturer Name"
                    customValidateFunc={charLim100}
                />
                <Field
                    component={Input}
                    name={`${accessor}.system.${currentPrimarySystem}.model`}
                    type="text"
                    label="Model Name"
                    placeholder="Enter Model Name"
                    customValidateFunc={charLim100}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.system.${currentPrimarySystem}.energyStar`}
                    label="Energy Star"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.system.${currentPrimarySystem}.epaCsa`}
                    label="EPA/CSA"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                    disabled={energySourceId <= 3}
                    info={"For solid fuel-fired equipment"}
                />
            </InputRow>
            <InputRow gridTemplate="23.4% 23.4% 10rem 23.4%">
                <Field
                    component={Select}
                    name={`${accessor}.system.${currentPrimarySystem}.energySource`}
                    options={getOptions({ fieldKey: `${currentPrimarySystem}EnergySource` })}
                    label="Energy Source"
                    placeholder="Choose Energy Source"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "energySource" })}
                    onChange={({ id }) => {
                        const firstOption = getFirstOption({ indKey: id, depKey: `${currentPrimarySystem}EquipType` });
                        change(`${accessor}.system.${currentPrimarySystem}.equipmentType`, firstOption);

                        const {
                            efficiency = 80,
                            pilotLight = 0,
                            flueDiameter = 0,
                        } = getDefaultFurnaceSpecs(firstOption.id) || {};
                        change(`${accessor}.system.${currentPrimarySystem}.specifications.efficiency`, efficiency);
                        change(`${accessor}.system.${currentPrimarySystem}.specifications.pilotLight`, pilotLight);
                        change(`${accessor}.system.${currentPrimarySystem}.specifications.flueDiameter`, flueDiameter);
                    }}
                />
                <Field
                    component={Select}
                    name={`${accessor}.system.${currentPrimarySystem}.equipmentType`}
                    options={getOptions({ fieldKey: `${currentPrimarySystem}EquipType`, indKey: energySourceId })}
                    label="Equipment Type"
                    placeholder="Choose Equipment Type"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "equipType" })}
                    onChange={({ id }) => {
                        const { efficiency = 80, pilotLight = 0, flueDiameter = 0 } = getDefaultFurnaceSpecs(id) || {};
                        change(`${accessor}.system.${currentPrimarySystem}.specifications.efficiency`, efficiency);
                        change(`${accessor}.system.${currentPrimarySystem}.specifications.pilotLight`, pilotLight);
                        change(`${accessor}.system.${currentPrimarySystem}.specifications.flueDiameter`, flueDiameter);
                    }}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.system.${currentPrimarySystem}.isBiEnergy`}
                    label="Dual Fuel System"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                    disabled={disableDualFuel}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.${currentPrimarySystem}.switchoverTemp`}
                    label="Dual Fuel Switchover Temperature"
                    placeholder="0.00"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "switchoverTemp" })}
                    decimals={getDecimalPlaces(`${currentPrimarySystem}SwitchoverTemp`)}
                    change={change}
                    disabled={!isDualFuel}
                    units={{
                        base: getBaseUnits(`${currentPrimarySystem}SwitchoverTemp`, modelUnits),
                        options: getUnitOptions("temperature"),
                        selected: switchoverTempUnits,
                        unitType: "temperature",
                        accessor: `${accessor}.system.${currentPrimarySystem}.switchoverTemp_u`,
                    }}
                    info={
                        "A system in which electric baseboards function until a specified (switchover) outdoor temperature is reached. Not permitted with heat pumps."
                    }
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.outputCapacity`}
                    options={[
                        {
                            label: "User Specified",
                            value: {
                                id: 0,
                                value: capacityValue,
                                uiUnits: "kW",
                            },
                        },
                        {
                            label: "Calculated",
                            value: {
                                id: 1,
                                value: capacityValue,
                                uiUnits: "kW",
                            },
                        },
                    ]}
                    label="Capacity Type"
                    placeholder="Choose Input Type"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "outputCapacity" })}
                    onChange={({ id }) => {
                        if (id === 1) {
                            change(`${accessor}.fansAndPumps.power.isCalculated`, true);
                        }
                    }}
                    info={
                        "If the capacity is unknown, set field to Calculated. Note that the calculated value will not appear in Volta SNAP, but will be updated prior to simulating in HOT2000."
                    }
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.outputCapacity.value`}
                    label="Output Capacity"
                    placeholder="0.00"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "outputCapacity" })}
                    decimals={getDecimalPlaces(`${currentPrimarySystem}OutputCapacity`)}
                    change={change}
                    disabled={capacityId === 1}
                    units={{
                        base: getBaseUnits(`${currentPrimarySystem}OutputCapacity`, modelUnits),
                        options: getUnitOptions("power"),
                        selected: capacityUnits,
                        unitType: "power",
                        accessor: `${accessor}.system.${currentPrimarySystem}.specifications.outputCapacity_u`,
                    }}
                />
                <Field
                    component={Input}
                    type="number"
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.sizingFactor`}
                    label="Sizing Factor"
                    placeholder={0.0}
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "sizingFactor" })}
                    decimals={getDecimalPlaces(`${currentPrimarySystem}SizingFactor`)}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.efficiency`}
                    label="Efficiency"
                    placeholder="0.00"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "efficiency" })}
                    decimals={getDecimalPlaces(`${currentPrimarySystem}Eff`)}
                    change={change}
                    setValue={effValue}
                    units={{
                        base: getBaseUnits(`${currentPrimarySystem}Eff`, modelUnits),
                        options: ["%"],
                        selected: "%",
                        unitType: "fraction",
                        accessor: `${accessor}.system.${currentPrimarySystem}.specifications.efficiency_u`,
                    }}
                    warning={energySourceId === 2 && isRecentVintage ? "oilHeatingLowEff" : ""}
                />
                <Field
                    component={Select}
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.isSteadyState`}
                    options={[
                        {
                            label: "Steady State",
                            value: true,
                        },
                        {
                            label: "AFUE",
                            value: false,
                        },
                    ]}
                    label="Efficiency Type"
                    placeholder="Choose Efficiency Type"
                    info={
                        "For condensing systems, always use the AFUE rating. For non-condensing systems, use AFUE whenever possible."
                    }
                    warning={equipTypeId === 5 && isSteadyState ? "condensingHeatingAfue" : ""}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.pilotLight`}
                    label="Pilot Light"
                    placeholder="0.00"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "pilotLight" })}
                    decimals={getDecimalPlaces(`${currentPrimarySystem}PilotLight`)}
                    change={change}
                    disabled={false}
                    setValue={pilotLightValue}
                    units={{
                        base: getBaseUnits(`${currentPrimarySystem}PilotLight`, modelUnits),
                        options: getUnitOptions("dailyEnergy"),
                        selected: pilotUnits,
                        unitType: "dailyEnergy",
                        accessor: `${accessor}.system.${currentPrimarySystem}.specifications.pilotLight_u`,
                    }}
                    info={"Leave at the default value (variable based on system)."}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.system.${currentPrimarySystem}.specifications.flueDiameter`}
                    label="Flue Diameter"
                    placeholder="0.00"
                    fieldKey={selectValidation({ system: currentPrimarySystem, field: "flueDiameter" })}
                    decimals={getDecimalPlaces(`${currentPrimarySystem}FlueDiameter`)}
                    change={change}
                    disabled={false}
                    setValue={flueDiameterValue}
                    units={{
                        base: getBaseUnits(`${currentPrimarySystem}FlueDiameter`, modelUnits),
                        options: getUnitOptions("length"),
                        selected: flueUnits,
                        unitType: "length",
                        accessor: `${accessor}.system.${currentPrimarySystem}.specifications.flueDiameter_u`,
                    }}
                    info={"Leave at the default value (variable based on system)."}
                />
            </InputRow>
        </>
    );
};
